/**
 * Organizational FAIR Maturity — client-side contract (DC-1613 2d).
 *
 * Mirrors `server/organizationalMaturity/types.ts` (DimensionPlacement — the
 * §6.3 three-value placement) + the reference payload served at
 * GET /api/organizational-maturity/reference. The client renders these
 * shapes; it never re-derives placements and never re-authors vendored
 * catalog text (CC BY 4.0 — matrix spec §2).
 */

export const MATURITY_DIMENSION_IDS = ['d1', 'd2', 'd3', 'd4', 'd5', 'd6', 'd7'] as const;
export type MaturityDimensionId = (typeof MATURITY_DIMENSION_IDS)[number];

export type MaturityLevelNumber = 0 | 1 | 2 | 3 | 4 | 5;
export const MATURITY_LEVEL_NUMBERS: readonly MaturityLevelNumber[] = [0, 1, 2, 3, 4, 5];

export type CoverageSufficiency = 'sufficient' | 'preliminary' | 'insufficient';

export interface RubricCriterion {
  id: string;
  dimension: MaturityDimensionId;
  level: MaturityLevelNumber;
  description: string;
  threshold: number;
  observed: number | null;
  satisfied: boolean;
  citation: {
    source_file: string;
    level_label: string;
    catalog_version: string;
  };
}

export interface DimensionPlacement {
  dimension: MaturityDimensionId;
  assessable: boolean;
  floor: MaturityLevelNumber | null;
  reach: MaturityLevelNumber | null;
  confidence: CoverageSufficiency;
  method: 'deterministic_rubric' | 'pending';
  rubric_version: string | null;
  criteria: RubricCriterion[];
  blocking: RubricCriterion[];
  evidence_summary: string;
}

// ---------------------------------------------------------------- evidence

export interface D1DomainReading {
  domain_id: string;
  domain_name: string;
  evaluated_datasets: number;
  total_datasets: number;
  mean: number;
  material: boolean;
}

export interface D1ComplianceSummary {
  documents_analyzed: number;
  datasets_analyzed: number;
  overall_compliance_score: number;
  per_pillar: Array<{
    pillar: 'F' | 'A' | 'I' | 'R';
    benchmark: number;
    practice: number;
    status: 'exceeds' | 'on_track' | 'gap' | 'critical_gap';
  }>;
}

export interface D1Evidence {
  kind: 'd1_technical';
  weighted_mean: number | null;
  min_material_domain: D1DomainReading | null;
  domains: D1DomainReading[];
  evaluated_datasets: number;
  total_datasets: number;
  compliance: D1ComplianceSummary | null;
}

export interface PendingDimensionEvidence {
  kind: 'pending';
  reason: string;
}

export type DimensionEvidence = D1Evidence | PendingDimensionEvidence;

export interface OrgEvidenceBundle {
  org_id: string;
  generated_at: string;
  per_dimension_evidence: Record<MaturityDimensionId, DimensionEvidence>;
  provenance: Array<{
    dimension: MaturityDimensionId;
    source_type: string;
    source_id: string | null;
    source_label: string;
    detail: string | null;
  }>;
}

export interface PlacementResponse {
  bundle: OrgEvidenceBundle;
  placements: DimensionPlacement[];
}

// ---------------------------------------------------------------- reference

export interface FairMmReferenceLevel {
  level: MaturityLevelNumber;
  name: string;
  nickname: string | null;
  marketplace_metaphor: string | null;
  dimensions: Record<MaturityDimensionId, string | null>;
}

export interface FairMmReference {
  catalog_version: string;
  license: string;
  source_repository: string;
  dimensions: Array<{
    id: MaturityDimensionId;
    name: string;
    key_notions: string | null;
  }>;
  levels: FairMmReferenceLevel[];
}

// ---------------------------------------------------------------- runs

export interface OrgMaturityAssessmentRow {
  id: string;
  run_id: string;
  dimension_key: MaturityDimensionId;
  assessable: boolean;
  inferred_level: number | null;
  reach_level: number | null;
  confidence: CoverageSufficiency;
  method: 'deterministic_rubric' | 'pending';
  rubric_version: string | null;
  self_claimed_level: number | null;
  evidence_summary: string;
  created_by: string | null;
  created_at: string;
}

export interface PersistedRun {
  run_id: string;
  assessments: OrgMaturityAssessmentRow[];
}
